import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { useCallback, useMemo } from 'react'
import Card from 'app/frontend/base/components/Card'
import RichTextContent from 'app/frontend/base/components/RichTextContent'
import { hasNotesContent } from 'app/frontend/base/components/NotesEditor'
import { Separator } from '@/components/ui/separator'
import { Badge } from '@/components/ui/badge'
import { getClass } from 'app/frontend/base/services/characterValues'
import { getTimelineSpellNameById } from 'app/frontend/base/services/raidPlanData'
import { getTimelineSpellsById } from 'app/frontend/base/services/raidPlanData'
import {
  ActorTimelineRow,
  sortActorTimelineRows,
} from '../../TeamPlans/components/ActorTimelineContainer'
import EventActivityPlanAbilityRow from './EventActivityPlanAbilityRow'
import ReadonlyCooldownTimeline from '../../TeamPlans/components/ReadonlyCooldownTimeline'
import { resolveLinkedPlanTimelineMaxTime } from '../hooks/resolveLinkedPlanEncounterAssignmentRows'
import { ActivityAssignmentViewModel } from './activityAssignmentViewModel'
import { capitalize } from 'lodash'
import { imageUrl } from 'app/frontend/base/helpers'
import { cn } from '@/lib/utils'
import DialogEncounterCard from './DialogEncounterCard'

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
  encounters: ActivityAssignmentViewModel[]
  selectedActivityId: number | null
  onSelectActivity: (activityId: number) => void
  participantName: string
}

const ViewerParticipantAssignmentAbilitiesDialog = ({
  open,
  onOpenChange,
  encounters,
  selectedActivityId,
  onSelectActivity,
  participantName,
}: Props) => {
  const activeEncounter = useMemo(
    () =>
      encounters.find(
        (encounter) => encounter.activity.id === selectedActivityId,
      ) ?? encounters[0],
    [encounters, selectedActivityId],
  )
  const activity = activeEncounter?.activity
  const linkedPlanData = activeEncounter?.linkedPlanData
  const characterId = activeEncounter?.displayCharacter.id
  const raidPlans = linkedPlanData?.raidPlans ?? []
  const selectedParticipants = linkedPlanData?.selectedParticipants ?? []
  const spellById = useMemo(() => getTimelineSpellsById(), [])
  const classColor = useCallback((classId?: number) => {
    if (classId === undefined || classId === null) return 'var(--color-primary)'
    const className = getClass(classId.toString())
    if (!className) return 'var(--color-primary)'
    return `var(--color-class-${className.toLowerCase().replace(/\s+/g, '-')})`
  }, [])
  const spellNameById = useMemo(() => getTimelineSpellNameById(), [])
  const participantCooldownRows = useMemo(() => {
    if (characterId === undefined) return []
    const matchedActors = raidPlans
      .flatMap((raidPlanData) => raidPlanData.resolvedActors)
      .filter(
        (resolvedActor) =>
          resolvedActor.participant?.characterId === characterId,
      )
    const rowsBySpellId: Record<string, ActorTimelineRow> = {}
    const upsertRow = (spellId: number | null) => {
      const key = spellId === null ? 'spell:null' : `spell:${spellId}`
      if (!rowsBySpellId[key]) {
        rowsBySpellId[key] = {
          key: `participant-cooldown-${key}`,
          spellId,
          casts: [],
        }
      }
      return rowsBySpellId[key]
    }
    matchedActors.forEach((resolvedActor) => {
      resolvedActor.actor.casts.forEach((cast) => {
        const spellId = cast.spell_id
        const row = upsertRow(spellId)
        const spell = spellId === null ? undefined : spellById[spellId]
        row.casts.push({
          id: cast.id,
          spellId,
          at: cast.at,
          duration: spell?.duration ?? 0,
          cooldown: spell?.cooldown ?? 0,
          color: classColor(resolvedActor.actor.class_id),
          issueType: 'none',
          message: cast.message,
          ttsMessage: cast.tts_message,
          rgb: cast.rgb,
          targets: cast.targets,
        })
      })
    })
    raidPlans.forEach((raidPlanData) => {
      ;(raidPlanData.linkedRaidPlan.timeline.general?.casts ?? []).forEach(
        (cast) => {
          const spellId = cast.spell_id
          const row = upsertRow(spellId)
          const spell = spellId === null ? undefined : spellById[spellId]
          row.casts.push({
            id: `general-${raidPlanData.linkedRaidPlan.id}-${cast.id}`,
            spellId,
            at: cast.at,
            duration: spell?.duration ?? 0,
            cooldown: spell?.cooldown ?? 0,
            color: 'var(--color-primary)',
            issueType: 'none',
            message: cast.message,
            ttsMessage: cast.tts_message,
            rgb: cast.rgb,
            targets: cast.targets,
          })
        },
      )
    })
    return sortActorTimelineRows(
      Object.values(rowsBySpellId),
      (spellId) =>
        spellId === null
          ? 'Message'
          : spellNameById[spellId] ?? `Spell ${spellId}`,
      (spellId) => (spellId === null ? 0 : spellById[spellId]?.cooldown ?? 0),
    )
  }, [characterId, raidPlans, classColor, spellById, spellNameById])
  const participantCooldownMaxTime = useMemo(() => {
    if (raidPlans.length > 0) {
      return Math.max(
        ...raidPlans.map((raidPlanData) =>
          resolveLinkedPlanTimelineMaxTime({
            raidPlan: raidPlanData.linkedRaidPlan,
            timeline: raidPlanData.linkedRaidPlan.timeline,
          }),
        ),
        1,
      )
    }
    return Math.max(
      participantCooldownRows.reduce((maxValue, row) => {
        const rowMax = row.casts.reduce((castMax, cast) => {
          return Math.max(
            castMax,
            cast.at + Math.max(cast.cooldown, cast.duration, 0),
          )
        }, 0)
        return Math.max(maxValue, rowMax)
      }, 0),
      1,
    )
  }, [raidPlans, participantCooldownRows])
  const hasAssignments = raidPlans.some(
    (raidPlanData) => raidPlanData.linkedPlanAssignments.length > 0,
  )
  const hasCooldowns = participantCooldownRows.length > 0
  const hasActivityNotes = hasNotesContent(activity?.notes)
  const planMessages = raidPlans
    .map((raidPlanData) => raidPlanData.linkedRaidPlan.message)
    .filter((message) => hasNotesContent(message))
  const hasPlanMessage = planMessages.length > 0
  const hasNotes = hasActivityNotes || hasPlanMessage
  const encounterName =
    activity?.encounter?.name ||
    activity?.instance?.name ||
    capitalize(activity?.activityType ?? 'Assignments')

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className='flex h-[90vh] max-h-[90vh] w-[90vw] max-w-[1200px] flex-col'
        onOpenAutoFocus={(event) => {
          event.preventDefault()
        }}
      >
        <DialogHeader className='shrink-0'>
          <DialogTitle>{participantName}&apos;s assignments</DialogTitle>
        </DialogHeader>
        <div className='min-h-0 flex-1 overflow-y-auto px-2 pt-2'>
          {encounters.length > 1 ? (
            <div className='mb-4 flex min-w-0 flex-wrap gap-2 sm:flex-nowrap'>
              {encounters.map((encounter) => (
                <DialogEncounterCard
                  key={encounter.activity.id}
                  viewModel={encounter}
                  isActive={
                    encounter.activity.id === activeEncounter?.activity.id
                  }
                  onClick={() => onSelectActivity(encounter.activity.id)}
                />
              ))}
            </div>
          ) : null}
          <div className='mb-4 flex items-center gap-2'>
            {activity ? (
              <img
                src={
                  activity.encounter?.thumbnailUrl ??
                  activity.instance?.tileUrl ??
                  imageUrl(`activity-thumbnails/${activity.activityType}.jpg`)
                }
                className='h-8 w-8 shrink-0 rounded-md border-2 border-border'
              />
            ) : null}
            <h4 className='mb-0'>{encounterName}</h4>
            <Badge
              variant={activeEncounter?.isSelected ? 'success' : 'warning'}
              className='h-6'
            >
              {activeEncounter?.isSelected ? 'Selected' : 'Benched'}
            </Badge>
          </div>
          {hasNotes ? (
            <div
              key={`notes-${activity?.id}`}
              className='flex flex-col gap-1 mb-4'
            >
              <span className='text-sm text-muted-foreground'>Message</span>
              <Card>
                {hasActivityNotes ? (
                  <RichTextContent
                    key={`activity-notes-${activity?.id}`}
                    content={activity?.notes ?? ''}
                  />
                ) : null}
                {hasActivityNotes && hasPlanMessage ? (
                  <Separator className='my-4' />
                ) : null}
                {planMessages.map((message, index) => (
                  <div key={`plan-message-${activity?.id}-${index}`}>
                    {index > 0 ? <Separator className='my-4' /> : null}
                    <RichTextContent
                      key={`plan-message-content-${activity?.id}-${index}`}
                      content={message ?? ''}
                    />
                  </div>
                ))}
              </Card>
            </div>
          ) : null}
          {!hasAssignments && !hasCooldowns ? (
            <div className='text-sm text-muted-foreground'>
              There are no assignments for this encounter.
            </div>
          ) : (
            <>
              {hasAssignments
                ? raidPlans.map((raidPlanData) => {
                    const linkedRaidPlan = raidPlanData.linkedRaidPlan
                    const visualOwner =
                      linkedRaidPlan.ownerType === 'User'
                        ? ({ ownerType: 'user' } as const)
                        : ({
                            ownerType: 'team',
                            ownerId: linkedRaidPlan.ownerId,
                          } as const)
                    return raidPlanData.linkedPlanAssignments.map((entry) => (
                      <EventActivityPlanAbilityRow
                        key={`${linkedRaidPlan.id}:${entry.abilityKey}`}
                        row={entry.row}
                        ability={entry.ability}
                        owner={visualOwner}
                        phaseDividers={raidPlanData.phaseDividers}
                        resolvedByScopeBoxKey={
                          raidPlanData.resolverResult.resolvedByScopeBoxKey
                        }
                        selectedParticipants={selectedParticipants}
                        onOverrideSelect={() => undefined}
                        onResetAbilityOverrides={() => undefined}
                        onSetAbilityHidden={() => undefined}
                        isReadonly
                      />
                    ))
                  })
                : null}
              <div className={cn('mb-12', hasAssignments ? 'mt-6' : '')}>
                <h4>Cooldowns</h4>
                {hasCooldowns ? (
                  <ReadonlyCooldownTimeline
                    rows={participantCooldownRows}
                    maxTime={participantCooldownMaxTime}
                    spellNameById={spellNameById}
                    timelineMinWidthClass='min-w-[25rem]'
                  />
                ) : (
                  <div className='text-sm text-muted-foreground'>
                    You don&apos;t have any cooldown casts for this encounter.
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default ViewerParticipantAssignmentAbilitiesDialog
